import type { components } from './generated/schema'

/**
 * `GET/PATCH /api/v1/users/me/notification-preferences` — the boundary.
 *
 * The wire is already cents (no float to convert), so this module's job is to
 * refuse a value the screen could not honestly show, and to build the ONLY
 * three shapes of PATCH the screen may send. The endpoint is partial and
 * `disabled_types` replaces the whole list, so a patch is always one field.
 *
 * `NotificationKind` comes from the generated schema, and `KIND_LABELS` is a
 * `Record` over it: a type the backend adds fails the type-check here until
 * someone decides its copy. Low balance is not a kind — the backend has no such
 * field — and "other types are always sent" so they get no switch.
 */
export type WireNotificationPreferences = components['schemas']['NotificationPreferences']
export type NotificationPreferencesPatch = components['schemas']['NotificationPreferencesUpdate']
export type NotificationKind = WireNotificationPreferences['disabled_types'][number]
export type SummaryFrequency = WireNotificationPreferences['spending_summary_frequency']

export const MAX_THRESHOLD_CENTS = 100_000_000

/** Label and one help line per switchable kind, in display order. The only copy table. */
export const KIND_LABELS: Record<NotificationKind, { label: string; help: string }> = {
  large_transaction: { label: 'Large transactions', help: 'When a transaction is over your threshold.' },
  suspicious_transaction: { label: 'Suspicious transactions', help: 'When something looks unusual on an account.' },
  transactions_synced: { label: 'New transactions', help: 'When new transactions arrive from your bank.' },
  recurring_paid: { label: 'Recurring payment made', help: 'When a recurring bill or income is matched.' },
  recurring_due_soon: { label: 'Recurring payment due soon', help: 'A heads-up before a recurring bill is due.' },
  recurring_missed: { label: 'Recurring payment missed', help: 'When an expected recurring payment does not show up.' },
  relink_reminder: { label: 'Reconnect reminders', help: 'When a bank connection needs to be reconnected.' },
  budget_period_reset: { label: 'Budget period reset', help: 'When a budget starts a new period.' },
  spending_summary: { label: 'Spending summary', help: 'A regular recap of your spending.' },
}

export const NOTIFICATION_KINDS = Object.keys(KIND_LABELS) as NotificationKind[]

export const isKind = (value: string): value is NotificationKind => Object.hasOwn(KIND_LABELS, value)

export type NotificationPreferences = {
  types: { kind: NotificationKind; enabled: boolean }[]
  /** Known disabled kinds in saved order — the list a toggle patch is built from. */
  disabled: NotificationKind[]
  /**
   * EVERY disabled type the server sent, in wire order, including ones this bundle has no copy for. Never rendered;
   * it exists so a whole-list replacement does not silently re-enable a type a newer client or backend disabled.
   */
  disabledWire: string[]
  largeTransactionThresholdCents: number
  spendingSummaryFrequency: SummaryFrequency
}

export function isValidThresholdCents(cents: number): boolean {
  return Number.isSafeInteger(cents) && cents >= 0 && cents <= MAX_THRESHOLD_CENTS
}

/**
 * Throws on a threshold or frequency the server's contract forbids, so the
 * screen lands in its error state rather than rendering a wrong figure. A type
 * string this build does not know is not rendered and not defaulted, but it stays
 * in `disabledWire`: `disabled_types` replaces the whole list, so leaving it out of
 * a patch would re-enable that notification.
 */
export function parseNotificationPreferences(wire: WireNotificationPreferences): NotificationPreferences {
  const cents = wire.large_transaction_threshold_cents
  if (!isValidThresholdCents(cents)) {
    throw new Error('notification-preferences: threshold is not valid integer cents')
  }
  const frequency = wire.spending_summary_frequency
  if (frequency !== 'weekly' && frequency !== 'monthly') {
    throw new Error('notification-preferences: unknown summary frequency')
  }
  const disabled = wire.disabled_types.filter((t): t is NotificationKind => isKind(t))
  return {
    types: NOTIFICATION_KINDS.map((kind) => ({ kind, enabled: !disabled.includes(kind) })),
    disabled,
    disabledWire: [...wire.disabled_types],
    largeTransactionThresholdCents: cents,
    spendingSummaryFrequency: frequency,
  }
}

/** The full disabled list with `kind` set on/off; types this bundle does not know are carried through in place. */
export function toggleDisabled(latestDisabled: readonly string[], kind: NotificationKind, enabled: boolean): string[] {
  const rest = latestDisabled.filter((k) => k !== kind)
  return enabled ? rest : [...rest, kind]
}

/** The patch for one toggle, computed from the latest list given. Unknown types ride along past the closed body type. */
export function togglePatch(
  latestDisabled: readonly string[],
  kind: NotificationKind,
  enabled: boolean,
): NotificationPreferencesPatch {
  return { disabled_types: toggleDisabled(latestDisabled, kind, enabled) as NotificationKind[] }
}

export const thresholdPatch = (cents: number): NotificationPreferencesPatch => ({
  large_transaction_threshold_cents: cents,
})

export const frequencyPatch = (frequency: SummaryFrequency): NotificationPreferencesPatch => ({
  spending_summary_frequency: frequency,
})
